import { SearchParamsOptions } from '@de/utils/types';

export type ReadableSearchParams = string | Record<string, string>;

/** Maps custom-field names (or raw search-param keys) to Option objects. */

/**
 * Parses external search parameters into an Option map keyed by param name.
 *
 * The returned map is NOT stored in flow state. Callers may pass it as the
 * `searchParamsOptions` fallback to `getOptionByCustomField` so that
 * dynamic-fetch utilities that support this argument can resolve search-param
 * values in the same way they resolve custom-field values from user selections.
 *
 * Placeholder translation currently reads only from `flowState` and does not
 * consult this map.
 *
 * When a key appears multiple times (e.g. `skills=A&skills=B`), its entry is
 * an `Option[]` so callers can treat it the same as a multi-select step answer.
 */
export const parseSearchParamsOptions = (
  searchParams?: ReadableSearchParams,
): SearchParamsOptions => {
  if (!searchParams) return {};

  const params = parseSearchParams(searchParams);
  const options: SearchParamsOptions = {};

  for (const [field, value] of Object.entries(params)) {
    if (!value) continue;

    if (Array.isArray(value)) {
      options[field] = value.map((v, i) => ({
        optionId: `${field}-search-param-${i}`,
        text: v,
        value: v,
        group: `${field}-search-param`,
      }));
    } else {
      options[field] = {
        optionId: `${field}-search-param`,
        text: value,
        value,
        group: `${field}-search-param`,
      };
    }
  }

  return options;
};

/**
 * Parses a searchParams prop (query string or object) into a key→value(s) map.
 * Duplicate keys (e.g. `skills=A&skills=B`) are collected into an array.
 */
const parseSearchParams = (
  searchParams: ReadableSearchParams,
): Record<string, string | string[]> => {
  if (typeof searchParams === 'object') return searchParams;
  const entries = new URLSearchParams(searchParams);
  const result: Record<string, string | string[]> = {};
  entries.forEach((value, key) => {
    const existing = result[key];
    if (existing === undefined) {
      result[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      result[key] = [existing, value];
    }
  });
  return result;
};
